import { useEffect, useRef } from "react";
import { Modal, Skeleton, radius } from "@libertify/ui";
import { useTranslate } from "@refinedev/core";
import { Trans } from "react-i18next";
import styled from "styled-components";
import { XIcon, InfoIcon, CheckCircleIcon } from "@phosphor-icons/react";
import mainBg from "@/assets/images/main-background.png";
import discountBg from "@/assets/bkg-discount.png";
import officeBg from "@/assets/office.png";
import { LibertifyLogo } from "pages/auth/components/LibertifyLogo";
import { useSession } from "hooks/useSession";
import useInitSubscription from "pages/pricing/hooks/useInitSubscription";
import { useStripePricingScript } from "@/hooks/useStripePricingScript";
import { useSubscriptionPolling } from "@/hooks/useSubscriptionPolling";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { NeedHelpCard } from "./NeedHelpCard";
import { useAnalytics } from "hooks/useAnalytics/";
import { AnalyticsEvents } from "types/analytics/";

export type SoftPaywallModalProps = {
  open: boolean;
  onClose: () => void;
  experiencesLeft: number;
  paywallFlow: string;
};

export const SoftPaywallModal = ({
  open,
  onClose,
  experiencesLeft,
  paywallFlow,
}: SoftPaywallModalProps) => {
  const t = useTranslate();
  const { currentOrganization: organization } = useSession();
  const { params, isLoading, init } = useInitSubscription({
    organizationId: organization?.id ?? "",
  });
  const { variant } = useSoftPaywall();
  const { trackEvent } = useAnalytics();

  useStripePricingScript(open);
  useSubscriptionPolling(open);

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (open && organization?.id && !hasInitialized.current) {
      hasInitialized.current = true;
      init();
    }
  }, [open, organization?.id, init]);

  const pricingTableId = import.meta.env
    .VITE_STRIPE_PRICING_TABLE_ID_ONLY_ONE_NO_TRIAL;

  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  const variantKey =
    variant === "expiredTrialPaywall"
      ? "pages.paywall.expiredTrialPaywall"
      : `components.softPaywallModal.variants.${variant}`;

  useEffect(() => {
    if (open) {
        trackEvent(AnalyticsEvents.PaywallView, {
    paywall_flow: paywallFlow,
    paywall_scenario: variant,
    paywall_hard: false,
    expired_trial: variant === "expiredTrialPaywall",
    pricing_table_id: pricingTableId,
  });
      trackEvent(AnalyticsEvents.PricingView, {
        container: "soft_paywall_modal",
        pricing_table_id: pricingTableId,
      });
    }
  }, [open, pricingTableId, trackEvent]);

  return (
    <StyledModal
      open={open}
      size="fullscreen"
      title={null}
      onClose={onClose}
      showFooter={false}
      maskClosable={false}
      closable={false}
    >
      <StyledShell>
        <StyledLeftCol>
          <StyledLogoBar>
            <LibertifyLogo />
          </StyledLogoBar>

          <StyledEyebrow>{t(`${variantKey}.eyebrow`)}</StyledEyebrow>
          <StyledHeroTitle>
            {t(
              variant === "expiredTrialPaywall"
                ? `${variantKey}.heroTitle`
                : `${variantKey}.title`
            )}
          </StyledHeroTitle>

          {variant === "expiredTrialPaywall" && (
            <ExpiredTrialContent t={t} variantKey={variantKey} />
          )}
          {variant === "specialOffer" && (
            <SpecialOfferContent t={t} variantKey={variantKey} />
          )}
          {variant === "lastFree" && (
            <LastFreeContent t={t} variantKey={variantKey} />
          )}
          {variant === "default" && (
            <DefaultContent t={t} experiencesLeft={experiencesLeft} />
          )}
        </StyledLeftCol>

        <StyledRightCol>
          <StyledCloseButton
            type="button"
            aria-label={t("buttons.close")}
            onClick={onClose}
          >
            <XIcon size={20} weight="bold" />
          </StyledCloseButton>
          <StyledPricingWrap>
            <StyledPricingCard>
              {isLoading || !params ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : (
                <stripe-pricing-table
                  pricing-table-id={pricingTableId}
                  publishable-key={publishableKey}
                  customer-session-client-secret={
                    params.customer_session_client_secret ?? undefined
                  }
                  client-reference-id={params.client_reference_id}
                  customer-email={params.customer_email}
                />
              )}
            </StyledPricingCard>
          </StyledPricingWrap>
          <StyledDismissLink
            onClick={() => {
              onClose();
              trackEvent(AnalyticsEvents.PaywallDismiss, {
                paywall_flow: paywallFlow,
                paywall_scenario: variant,
                paywall_hard: false,
                pricing_table_id: pricingTableId,
              });
            }}
          >
            {t(`${variantKey}.dismissLink`)}
          </StyledDismissLink>
        </StyledRightCol>
      </StyledShell>
    </StyledModal>
  );
};

/* ─── Variant-specific content blocks ─────────────────────────────────────── */

type ContentProps = {
  t: ReturnType<typeof useTranslate>;
  variantKey?: string;
  experiencesLeft?: number;
};

const DefaultContent = ({ t, experiencesLeft }: ContentProps) => (
  <>
    <StyledSubtitle>
      <Trans components={{ strong: <strong /> }}>
        {t("components.softPaywallModal.experiencesLeft", {
          count: experiencesLeft,
        })}
      </Trans>
    </StyledSubtitle>
    <StyledSubtitle>
      {t("components.softPaywallModal.description")}
    </StyledSubtitle>
    <StyledCta>{t("components.softPaywallModal.cta")}</StyledCta>
  </>
);

const SpecialOfferContent = ({ t, variantKey }: ContentProps) => (
  <>
    <StyledPromoBanner>
      <StyledPromoLogo src="/mini_logo.svg" alt="Libertify" />
      <StyledPromoPricing>
        <StyledPromoOldPrice>
          {t(`${variantKey}.promoOldPrice`)}
        </StyledPromoOldPrice>
        <StyledPromoNewPrice>
          {t(`${variantKey}.promoNewPrice`)}
        </StyledPromoNewPrice>
        <StyledPromoLabel>
          <Trans components={{ strong: <strong /> }}>
            {t(`${variantKey}.promoLabel`)}
          </Trans>
        </StyledPromoLabel>
      </StyledPromoPricing>
    </StyledPromoBanner>
    <StyledSubtitle>{t(`${variantKey}.subtitle`)}</StyledSubtitle>
    <StyledSubtitle>
      <Trans components={{ strong: <strong /> }}>
        {t(`${variantKey}.promoCode`)}
      </Trans>
    </StyledSubtitle>
    <StyledInfoRow>
      <InfoIcon size={16} weight="bold" />
      <span>{t(`${variantKey}.urgency`)}</span>
    </StyledInfoRow>
    <NeedHelpCard />
  </>
);

const EXPIRED_FEATURE_KEYS = [
  "documentToVideo",
  "aiChatbot",
  "journeyEngine",
  "brandedExperiences",
  "aiAvatar",
  "fullAnalytics",
  "customVoice",
  "shareableLink",
  "keepAccess",
] as const;

const ExpiredTrialContent = ({ t, variantKey }: ContentProps) => (
  <>
    <StyledHeroImage src={officeBg} alt="" />
    <StyledFeaturesCard>
      <StyledFeaturesCardTitle>
        {t(`${variantKey}.featuresTitle`)}
      </StyledFeaturesCardTitle>
      <StyledFeatureGrid>
        {EXPIRED_FEATURE_KEYS.map((key) => (
          <StyledFeatureItem key={key}>
            <CheckCircleIcon size={20} weight="fill" color="#14b8a6" />
            <span>{t(`${variantKey}.features.${key}`)}</span>
          </StyledFeatureItem>
        ))}
      </StyledFeatureGrid>
      <StyledSubtitleExpired style={{ margin: "24px 0 0" }}>
        {t(`${variantKey}.ctaDescription`)}
      </StyledSubtitleExpired>
    </StyledFeaturesCard>
  </>
);

const LastFreeContent = ({ t, variantKey }: ContentProps) => (
  <>
    <StyledSubtitle>{t(`${variantKey}.subtitle`)}</StyledSubtitle>
    <StyledSubtitle>{t(`${variantKey}.description`)}</StyledSubtitle>
    <NeedHelpCard />
  </>
);

/* ─── Styled components ───────────────────────────────────────────────────── */

const StyledModal = styled(Modal)`
  && .ant-modal-content {
    padding: 0;

    .ant-modal-body {
      padding: 0 !important;
    }
  }
`;

const StyledShell = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  font-family: "Inter Variable", "Inter", sans-serif;
  background-image: url(${mainBg});
  background-size: cover;
  background-position: center;

  @media (width <= 768px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

const StyledLeftCol = styled.div`
  flex: 0 0 55%;
  padding: clamp(32px, 5vh, 56px) clamp(40px, 5vw, 72px) clamp(40px, 6vh, 72px);
  display: flex;
  flex-direction: column;

  @media (width <= 768px) {
    flex: none;
    width: 100%;
    padding: 24px 20px;
  }
`;

const StyledRightCol = styled.div`
  flex: 0 0 45%;
  min-height: 100vh;
  padding: clamp(32px, 5vh, 56px) clamp(40px, 5vw, 72px) clamp(40px, 6vh, 72px)
    0;
  display: flex;
  flex-direction: column;

  @media (width <= 768px) {
    flex: none;
    width: 100%;
    min-height: 0;
    padding: 24px 20px 32px;
  }
`;

const StyledCloseButton = styled.button`
  align-self: flex-end;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: ${radius.circle};
  color: #090e19;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #0000000d;
  }
`;

const StyledPricingWrap = styled.div`
  align-self: center;
  width: 100%;
  max-width: 75%;
  margin: auto 0;
  display: flex;
  flex-direction: column;
`;

const StyledPricingCard = styled.div`
  flex: 1;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: ${radius.box}px;
  padding: 32px 28px;
  box-shadow: 0 4px 24px -8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 610px;

  @media (width <= 768px) {
    min-height: 400px;
  }
`;

const StyledLogoBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: clamp(32px, 5vh, 56px);

  svg {
    height: 32px;
    width: auto;
  }
`;

const StyledEyebrow = styled.span`
  display: inline-block;
  align-self: flex-start;
  font-size: 11px;
  font-weight: 700;
  color: #ffffff;
  background: #3b3f47;
  border-radius: ${radius.pill}px;
  padding: 4px 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const StyledHeroTitle = styled.h1`
  font-family: "Poppins", "Inter Variable", sans-serif;
  font-size: clamp(36px, 4vw, 56px);
  font-weight: 700;
  color: #090e19;
  letter-spacing: -0.03em;
  line-height: 1.05;
  margin: 0 0 24px;
`;

const StyledSubtitleExpired = styled.p`
  color: #374151;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 23.8px;
`;

const StyledSubtitle = styled.p`
  color: #090e19;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.16px;
  margin: 0 0 24px;

  strong {
    color: #090e19;
    font-weight: 700;
  }
`;

const StyledCta = styled.p`
  font-size: 16px;
  color: #090e19;
  font-weight: 700;
  line-height: 1.6;
  margin: 40px 0 16px;
`;

const StyledPromoBanner = styled.div`
  position: relative;
  background: #6d28d9 url(${discountBg}) center / cover no-repeat;
  align-items: center;
  display: flex;
  padding: 28px;
  margin-bottom: 24px;
  color: #ffffff;
  text-align: right;
`;

const StyledPromoLogo = styled.img`
  position: absolute;
  top: 16px;
  left: 20px;
  height: 35px;
  width: auto;
  opacity: 0.6;
`;

const StyledPromoPricing = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
`;

const StyledPromoOldPrice = styled.span`
  color: #fff;
  font-family: "Poppins", "Inter Variable", sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 400;
  text-decoration: line-through;
`;

const StyledPromoNewPrice = styled.p`
  font-family: "Poppins", "Inter Variable", sans-serif;
  font-size: 122px;
  font-weight: 700;
  margin: 0;
  line-height: 1;
`;

const StyledPromoLabel = styled.p`
  font-size: 16px;
  color: #fff;
  font-family: "Poppins", "Inter Variable", sans-serif;
  font-style: normal;
  font-weight: 400;
  line-height: 1.4;
  margin: 4px 0 0;

  strong {
    color: #fff;
    font-weight: 700;
  }
`;

const StyledInfoRow = styled.div`
  color: #595d66;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.16px;

  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const StyledHeroImage = styled.img`
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: ${radius.box}px ${radius.box}px 0 0;
  margin-bottom: 0;
`;

const StyledFeaturesCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 ${radius.box}px ${radius.box}px;
  padding: 28px;
  margin-bottom: 24px;
`;

const StyledFeaturesCardTitle = styled.p`
  font-family: "Poppins", "Inter Variable", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #090e19;
  letter-spacing: -0.18px;
  margin: 0 0 20px;
`;

const StyledFeatureGrid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (width <= 768px) {
    grid-template-columns: 1fr;
  }
`;

const StyledFeatureItem = styled.li`
  color: #374151;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.3px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    flex-shrink: 0;
  }
`;

const StyledDismissLink = styled.button`
  align-self: center;
  margin-top: 24px;
  background: none;
  border: none;
  font-size: 14px;
  color: #6b7280;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    color: #374151;
  }
`;
